
import Vue, { PropType } from "vue";
import { Comment, User, SharedLinkKind } from "@/lib/models";
import VueMarkdown from "vue-markdown";
import { format } from "date-fns";
import clone from "lodash/cloneDeep";

export default Vue.extend<Data, Methods, Computed, Props>({
  components: {
    VueMarkdown,
  },
  props: {
    sharedLinkId: {
      type: String as PropType<string>,
    },
    novelId: {
      type: String as PropType<string>,
    },
    contentKey: {
      type: String as PropType<string>,
    },
    kind: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      comment: "",
      editTarget: null,
      deleteTarget: null,
      isDeleting: false,
    };
  },
  methods: {
    post() {
      const { sharedLinkId, contentKey, novelId, comment } = this;
      const { name } = this.user;

      this.$store.dispatch("sharedLinkModule/createComment", {
        sharedLinkId,
        manuscriptKey: this.kind === SharedLinkKind.MANUSCRIPT ? contentKey : undefined,
        plotId: this.kind === SharedLinkKind.PLOT ? contentKey : undefined,
        novelId,
        name: name || "",
        comment,
        callback: async () => {
          this.comment = "";
          await this.$nextTick();
          this.scrollToBottom();
        },
      });
    },
    updateComment() {
      const { sharedLinkId, commentId, manuscriptKey, plotId, novelId, name, comment } = this.editTarget as Comment;

      this.$store.dispatch("sharedLinkModule/updateComment", {
        sharedLinkId,
        commentId,
        manuscriptKey,
        plotId,
        novelId,
        name,
        comment,
        callback: () => {
          this.initializeEditFragment();
        },
      });
    },
    onClickDelete(comment: Comment) {
      this.isDeleting = true;
      this.deleteTarget = comment;
    },
    cancelDelete() {
      this.initializeDeleteFragment();
    },
    deleteComment() {
      const { sharedLinkId, commentId } = this.deleteTarget as Comment;
      this.$store.dispatch("sharedLinkModule/deleteComment", {
        sharedLinkId,
        commentId,
        callback: () => {
          this.initializeEditFragment();
          this.initializeDeleteFragment();
        },
      });
    },
    initializeEditFragment() {
      this.editTarget = null;
    },
    initializeDeleteFragment() {
      this.isDeleting = false;
      this.deleteTarget = null;
    },
    async reply(comment: string) {
      // eslint-disable-next-line vue/no-parsing-error
      /** 文字の先頭に引用符を付ける */
      const splittedComment = comment.split("\n");
      const addedQuoteComment = splittedComment.map((comment) => `> ${comment}`);
      this.comment = `${addedQuoteComment.join("\n")}\n\n`;

      /** this.commentが反映されるまで待つ */
      await this.$nextTick();

      /** textareaにフォーカスする */
      const textareaElement = this.$refs.textarea;
      textareaElement.focus();
      textareaElement.scrollTop = textareaElement.scrollHeight;
    },
    async quote(comment: string) {
      /** 文字をバッククォートで囲む */
      this.comment = `\`\`\`\n${comment}\n\`\`\`\n\n`;

      /** this.commentが反映されるまで待つ */
      await this.$nextTick();

      /** textareaにフォーカスする */
      const textareaElement = this.$refs.textarea;
      textareaElement.focus();
      textareaElement.scrollTop = textareaElement.scrollHeight;
    },
    async quoteTextJoinComment(comment: string) {
      /** 文字をバッククォートで囲む */
      this.comment += `\`\`\`\n${comment}\n\`\`\`\n\n`;

      /** this.commentが反映されるまで待つ */
      await this.$nextTick();

      /** textareaにフォーカスする */
      const textareaElement = this.$refs.textarea;
      textareaElement.focus();
      textareaElement.scrollTop = textareaElement.scrollHeight;
    },
    edit(commentObj: Comment) {
      this.editTarget = clone(commentObj);
    },
    scrollToBottom() {
      const listElement = this.$refs.list;
      listElement.scrollTop = listElement.scrollHeight;
    },
  },
  computed: {
    commentList() {
      const commentList = this.$store.getters["sharedLinkModule/commentList"] as Comment[];
      switch (this.kind) {
        case SharedLinkKind.MANUSCRIPT:
          return commentList.filter((comment) => comment.manuscriptKey === this.contentKey);
        case SharedLinkKind.PLOT:
          return commentList.filter((comment) => comment.plotId === this.contentKey);
        default:
          return [];
      }
    },
    user() {
      return this.$store.getters["userModule/user"];
    },
    quoteText() {
      return this.$store.getters["sharedLinkModule/quoteText"];
    },
    rows() {
      return (comment: string) => {
        const breakNum = comment.split("\n").length;
        return breakNum < 10 ? breakNum : 10;
      };
    },
    createdAt() {
      return (date: string) => format(date, "YYYY/MM/DD HH:mm");
    },
    isEditing() {
      return (commentId: string) => this.editTarget && this.editTarget.commentId === commentId;
    },
    isOwnComment() {
      return (userId: string) => userId === this.user.userId;
    },
  },
  mounted() {
    this.scrollToBottom();
  },
  watch: {
    quoteText() {
      if (this.quoteText) {
        if (!this.comment) {
          this.quote(this.quoteText);
        } else {
          this.quoteTextJoinComment(this.quoteText);
        }
      }
    },
  },
});

interface Props {
  sharedLinkId: string;
  novelId: string;
  contentKey: string;
  kind: string;
}

interface Data {
  comment: string;
  editTarget: Comment | null;
  deleteTarget: Comment | null;
  isDeleting: boolean;
}

interface Computed {
  commentList: Comment[];
  user: User;
  quoteText: string;
  rows: (comment: string) => number;
  createdAt: (date: string) => string;
  isEditing: (commentId: string) => boolean;
  isOwnComment: (userId: string) => boolean;
}

interface Methods {
  post: () => void;
  onClickDelete: (comment: Comment) => void;
  cancelDelete: () => void;
  initializeDeleteFragment: () => void;
  initializeEditFragment: () => void;
  deleteComment: (comment: Comment) => void;
  updateComment: () => void;
  reply: (comment: string) => void;
  quote: (comment: string) => void;
  quoteTextJoinComment: (comment: string) => void;
  edit: (commentObj: Comment) => void;
  scrollToBottom: () => void;
}
